// src/components/Home/Profile/ProfileNormal.js

import React, { useEffect, useMemo, useState } from "react";
import API from "../../../api";
import {
  ArrowLeft,
  MapPin,
  Building2,
  Briefcase,
  Share2,
  MessageCircle,
  UserPlus,
  Users,
  BadgeCheck,
  Edit3,
  Phone,
  Mail
} from "lucide-react";

import AdvancedPortfolio from "./AdvancedPortfolio";
import "./ProfileNormal.css";

export default function ProfileNormal({
  user,
  posts = [],
  loadingPosts = false,
  onAddPost = () => {},
  onEditProfile = () => {},
  onEditPost = () => {},
  onDeletePost = () => {},
  portfolioLabel = "Posts"
}) {
  /* ======================================================
      USER
  ====================================================== */

  const loggedInUser = useMemo(() => {
    try {
      return JSON.parse(
        localStorage.getItem("civilink_user")
      );
    } catch {
      return null;
    }
  }, []);

  

  const isOwnProfile =
    loggedInUser?._id === user?._id;

  /* ======================================================
      STATES
  ====================================================== */

  const [connected, setConnected] =
    useState(false);

  const [imageLoaded, setImageLoaded] =
    useState(false);

  /* ======================================================
      CONNECTION STATUS
  ====================================================== */

  useEffect(() => {
    if (!loggedInUser || !user) return;

    setConnected(
      loggedInUser.connections?.includes(user._id)
    );
  }, [loggedInUser, user]);

  /* ======================================================
      CONNECT USER
  ====================================================== */

  const connectUser = async () => {
  try {
    const res = await API.post(
      `/connections/${user._id}/connect`
    );

    if (res.data.success) {
      setConnected(true);

      // Keep localStorage in sync
      if (loggedInUser) {
        const updatedUser = {
          ...loggedInUser,
          connections: [
            ...(loggedInUser.connections || []),
            user._id
          ]
        };

        localStorage.setItem(
          "civilink_user",
          JSON.stringify(updatedUser)
        );
      }
    }
  } catch (err) {
    console.error("Connect failed:", err);

    alert(
      err.response?.data?.message ||
      "Unable to connect."
    );
  }
};

  /* ======================================================
      SHARE PROFILE
  ====================================================== */

  const profileUrl =
    `${window.location.origin}/profile/${user._id}`;

  const shareProfile = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: `${user.name} | Civilink`,
          url: profileUrl
        });
      } else {
        await navigator.clipboard.writeText(
          profileUrl
        );

        alert("Profile link copied");
      }
    } catch (err) {
      console.error(err);
    }
  };

  /* ======================================================
      PORTFOLIO ACCESS
  ====================================================== */

  const canShowPortfolio =
    isOwnProfile ||
    !user.isProfessional ||
    user.professionalVerification?.status ===
      "approved";

  /* ======================================================
      USER INFO
  ====================================================== */

  const displayLocation = [
    user.location?.city,
    user.location?.state
  ]
    .filter(Boolean)
    .join(", ");

  const displayRole =
    user.profession ||
    user.designation ||
    user.role ||
    "Civilink Member";

  const displayCompany =
    user.company ||
    user.organization ||
    "";

  const verified =
    user.professionalVerification?.status ===
    "approved";

  /* ======================================================
      JSX
  ====================================================== */

  return (
    <div className="profile-page">

      {/* ================= COVER ================= */}

      <div className="profile-cover">

        <div className="cover-overlay" />

        <button
          className="profile-back-btn"
          onClick={() => window.history.back()}
        >
          <ArrowLeft size={20} />
        </button>

        {isOwnProfile && (
          <button
            className="profile-edit-btn"

  onClick={onEditProfile}
          >
            <Edit3 size={18} />
            Edit Profile
          </button>
        )}

      </div>

      {/* ================= PROFILE CARD ================= */}

      <div className="profile-main-card">

        <div className="profile-avatar-wrapper">

          <div className="profile-avatar">

            {user.profilePhoto ? (
              <img
                src={user.profilePhoto}
                alt={user.name}
                onLoad={() =>
                  setImageLoaded(true)
                }
                className={
                  imageLoaded
                    ? "avatar-image loaded"
                    : "avatar-image"
                }
              />
            ) : user.avatar ? (
              <img
                src={user.avatar}
                alt={user.name}
                onLoad={() =>
                  setImageLoaded(true)
                }
                className={
                  imageLoaded
                    ? "avatar-image loaded"
                    : "avatar-image"
                }
              />
            ) : (
              <span className="avatar-text">
                {user.name
                  ?.charAt(0)
                  ?.toUpperCase() || "U"}
              </span>
            )}

            {verified && (
              <div className="verified-badge">
                <BadgeCheck size={18} />
              </div>
            )}

          </div>
                    {/* ================= PROFILE DETAILS ================= */}

          <div className="profile-details">

            <div className="profile-title-row">

              <div>

                <h1 className="profile-name">
                  {user.name || "Civilink User"}
                </h1>

                <div className="profile-role">

                  <Briefcase size={16} />

                  <span>{displayRole}</span>

                </div>

                {displayCompany && (

                  <div className="profile-company">

                    <Building2 size={16} />

                    <span>{displayCompany}</span>

                  </div>

                )}

                {displayLocation && (

                  <div className="profile-location">

                    <MapPin size={16} />

                    <span>{displayLocation}</span>

                  </div>

                )}

              </div>

            </div>

            {/* ================= ACTION BUTTONS ================= */}

            <div className="profile-action-grid">

              {!isOwnProfile ? (

                <>

                  <button
                    className={`profile-btn ${
                      connected
                        ? "secondary"
                        : "primary"
                    }`}
                    disabled={connected}
                    onClick={connectUser}
                  >
                    <UserPlus size={18} />

                    {connected
                      ? "Connected"
                      : "Connect"}
                  </button>

                  <button
                    className="profile-btn secondary"
                  >
                    <MessageCircle size={18} />

                    Message
                  </button>

                </>

              ) : (

                <button
                  className="profile-btn secondary"
                  onClick={() =>
                    window.location.assign(
                      `/profile/${user._id}/connections`
                    )
                  }
                >
                  <Users size={18} />

                  Connections
                </button>

              )}

              <button
                className="profile-btn outline"
                onClick={shareProfile}
              >
                <Share2 size={18} />

                Share
              </button>

              {(user.phone || user.email) && (

                <button
                  className="profile-btn outline"
                  onClick={() => {

                    if (user.phone) {

                      window.location.href =
                        `tel:${user.phone}`;

                    } else if (user.email) {

                      window.location.href =
                        `mailto:${user.email}`;

                    }

                  }}
                >
                  {user.phone ? (
                    <Phone size={18} />
                  ) : (
                    <Mail size={18} />
                  )}

                  Contact
                </button>

              )}

            </div>

          </div>

        </div>

        {/* ================= ABOUT CARD ================= */}

        {user.bio && (

          <div className="profile-card">

            <div className="profile-card-header">

              <h3>About</h3>

            </div>

            <div className="profile-about">

              {user.bio}

            </div>

          </div>

        )}

        {/* ================= PORTFOLIO ================= */}

        <div className="profile-card">

          <div className="profile-card-header">

            <h3>{portfolioLabel}</h3>

          </div>

          {canShowPortfolio ? (

            <AdvancedPortfolio
  posts={posts}
  loading={loadingPosts}
  isOwnProfile={isOwnProfile}
  onAddPost={onAddPost}
  onEditPost={onEditPost}
  onDeletePost={onDeletePost}
/>

          ) : (

            <div className="portfolio-hidden">

              <BadgeCheck size={18} />

              <span>
                Portfolio is available after professional verification is approved.
              </span>

            </div>

          )}

        </div>

      </div>

    </div>

  );

}